// Recurrence expansion utilities for calendar events.
// Event recurrence shape:
// {
//   freq: 'daily' | 'weekly' | 'monthly' | 'yearly',
//   interval: number,              // every N freq-units (default 1)
//   weekdays: number[],             // only for weekly; ISO 1=Mon..7=Sun
//   endType: 'never' | 'count' | 'until',
//   count: number,                  // only for endType === 'count'
//   until: 'YYYY-MM-DD',            // only for endType === 'until'
// }

import {
  parseISO,
  format,
  addDays,
  addMonths,
  addYears,
  differenceInCalendarDays,
  startOfWeek
} from 'date-fns';

const MAX_OCCURRENCES = 2000;

const toStr = (d) => format(d, 'yyyy-MM-dd');

const stepBase = (baseStart, freq, interval, i) => {
  switch (freq) {
    case 'daily':
      return addDays(baseStart, interval * i);
    case 'weekly':
      return addDays(baseStart, interval * 7 * i);
    case 'monthly':
      return addMonths(baseStart, interval * i);
    case 'yearly':
      return addYears(baseStart, interval * i);
    default:
      return baseStart;
  }
};

// Map JS Date getDay (0=Sun..6=Sat) to ISO (1=Mon..7=Sun)
const isoWeekday = (date) => {
  const d = date.getDay();
  return d === 0 ? 7 : d;
};

/**
 * Expand a list of events (some of which have .recurrence) into concrete instances
 * overlapping the [rangeStart, rangeEnd] window.
 */
export function expandEvents(events, rangeStart, rangeEnd) {
  const startStr = toStr(rangeStart);
  const endStr = toStr(rangeEnd);
  const out = [];

  for (const ev of events) {
    if (!ev.recurrence || !ev.recurrence.freq) {
      // Non-recurring
      const evEnd = ev.endDate || ev.date;
      if (ev.date <= endStr && evEnd >= startStr) {
        out.push(ev);
      }
      continue;
    }

    const { freq, endType = 'never' } = ev.recurrence;
    const interval = Math.max(1, Number(ev.recurrence.interval) || 1);
    const weekdays = Array.isArray(ev.recurrence.weekdays)
      ? ev.recurrence.weekdays.filter((n) => n >= 1 && n <= 7)
      : [];
    const count = Math.max(1, Number(ev.recurrence.count) || 1);
    const untilStr = ev.recurrence.until || null;
    const untilDate = untilStr ? parseISO(untilStr) : null;

    const baseStart = parseISO(ev.date);
    const baseEnd = ev.endDate ? parseISO(ev.endDate) : baseStart;
    const duration = Math.max(0, differenceInCalendarDays(baseEnd, baseStart));

    const occurrences = [];

    if (freq === 'weekly' && weekdays.length > 0) {
      // Iterate week blocks (interval weeks each) starting at the ISO-week of baseStart
      const baseWeekMonday = startOfWeek(baseStart, { weekStartsOn: 1 });
      const sortedDays = [...weekdays].sort((a, b) => a - b);
      let weekIdx = 0;
      let produced = 0;
      outer: while (true) {
        const weekStart = addDays(baseWeekMonday, weekIdx * 7 * interval);
        // Stop if this entire week is past the range and past until
        if (weekStart > rangeEnd) break;
        for (const wd of sortedDays) {
          const occ = addDays(weekStart, wd - 1);
          if (occ < baseStart) continue;
          if (untilDate && occ > untilDate) break outer;
          if (endType === 'count' && produced >= count) break outer;
          occurrences.push(occ);
          produced++;
          if (produced > MAX_OCCURRENCES) break outer;
        }
        weekIdx++;
        if (weekIdx > 1000) break;
      }
    } else {
      // daily / weekly (no specific weekdays) / monthly / yearly
      let i = 0;
      while (true) {
        const occ = stepBase(baseStart, freq, interval, i);
        if (untilDate && occ > untilDate) break;
        if (endType === 'count' && i >= count) break;
        if (occ > rangeEnd) break;
        occurrences.push(occ);
        i++;
        if (i > MAX_OCCURRENCES) break;
      }
    }

    const exceptions = new Set(ev.recurrence.exceptions || []);
    for (const occStart of occurrences) {
      const occEnd = addDays(occStart, duration);
      const occStartStr = toStr(occStart);
      const occEndStr = toStr(occEnd);
      if (exceptions.has(occStartStr)) continue;
      if (occEndStr < startStr || occStartStr > endStr) continue;
      const isFirst = occStartStr === ev.date;
      out.push({
        ...ev,
        id: isFirst ? ev.id : `${ev.id}::${occStartStr}`,
        originalId: ev.id,
        date: occStartStr,
        endDate: occEndStr,
        isOccurrence: !isFirst
      });
    }
  }

  return out;
}

export const makePresetRecurrence = (preset, baseDateStr) => {
  switch (preset) {
    case 'daily':
      return { freq: 'daily', interval: 1, endType: 'never' };
    case 'weekly': {
      const wd = isoWeekday(parseISO(baseDateStr));
      return { freq: 'weekly', interval: 1, weekdays: [wd], endType: 'never' };
    }
    case 'monthly':
      return { freq: 'monthly', interval: 1, endType: 'never' };
    case 'yearly':
      return { freq: 'yearly', interval: 1, endType: 'never' };
    case 'custom': {
      const wd = isoWeekday(parseISO(baseDateStr));
      return { freq: 'weekly', interval: 1, weekdays: [wd], endType: 'never', custom: true };
    }
    default:
      return null;
  }
};

// Detect whether a recurrence matches one of the simple presets
// (daily/weekly/monthly/yearly, interval=1, never-ending, weekly only on baseDate's weekday)
export const detectPreset = (recurrence, baseDateStr) => {
  if (!recurrence || !recurrence.freq) return 'none';
  if (recurrence.custom) return 'custom';
  if (recurrence.endType && recurrence.endType !== 'never') return 'custom';
  if ((recurrence.interval || 1) !== 1) return 'custom';
  if (recurrence.freq === 'weekly') {
    const wd = isoWeekday(parseISO(baseDateStr));
    const wds = recurrence.weekdays || [];
    if (wds.length === 1 && wds[0] === wd) return 'weekly';
    return 'custom';
  }
  return recurrence.freq;
};

export { isoWeekday };
